import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { updateEventById, nextTab } from '../../../../../../app/store/slices/event/event';
import { errorToast, successToastTopRight } from '../../../../../../utils/toasts/toasts';
import { useEventStatus } from '../../../../../../hooks/event-hooks/useEventStatus';

export default function useMedia() {
    const dispatch = useDispatch();
    const { eventData } = useSelector((state) => state.event);
    const { user } = useSelector((state) => state.auth);
    const eventGalleryScrollRef = useRef(null);
    const [eventThumbnail, setEventThumbnail] = useState(null);
    const [eventLayout, setEventLayout] = useState(null);
    const [eventGalleryImages, setEventGalleryImages] = useState([null]);
    const [banner, setBanner] = useState(null); // store the event banner
    const [cropImage, setCropImage] = useState(null); // store the crop image
    const [cropType, setCropType] = useState(null); // 'thumbnail' or 'layout' or 'gallery'
    const [cropIndex, setCropIndex] = useState(null); // Index for gallery image being cropped
    const [isEditModeOn, setIsEditModeOn] = useState(true);
    const [shouldShowEditButton, setShouldShowEditButton] = useState(false);
    const [loading, setLoading] = useState(false);

    const { isEventDrafted, isMediaTabCompleted, isEventLive } = useEventStatus();

    useEffect(() => {
        const shouldShow = isEventDrafted && isMediaTabCompleted;
        setShouldShowEditButton(shouldShow);

        if (eventData?.publish || isEventLive) {
            // If published → always false
            setIsEditModeOn(false);
        } else if (!isMediaTabCompleted || !isEventDrafted) {
            // If not completed OR not drafted → allow editing
            setIsEditModeOn(true);
        } else {
            // Both drafted & completed → disable edit mode
            setIsEditModeOn(false);
        }
    }, [isEventDrafted, isEventLive, isMediaTabCompleted, eventData?.publish]);

    // initial values
    const initialValues = {
        thumbnail: null,
        layoutThumbnail: null,
        galleryImages: [],
        banner: ' '
    };

    // validation schema
    const validationSchema = Yup.object().shape({
        thumbnail: Yup.mixed().nullable(),
        layoutThumbnail: Yup.mixed().notRequired(),
        galleryImages: Yup.array().of(Yup.mixed()).notRequired(),
        banner: Yup.mixed().notRequired()
    });

    // manage formik form state and validation
    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                setLoading(true);
                if (!values.thumbnail) {
                    errorToast('Event thumbnail is required');
                    return;
                }

                // Create a FormData object
                const formData = new FormData();
                formData.append('step', 'media');
                formData.append('user_id', user?.id);

                if (values.thumbnail) {
                    formData.append('thumbnail', values.thumbnail);
                }

                if (values.layoutThumbnail) {
                    formData.append('event_layout', values.layoutThumbnail);
                }

                if (values.banner) {
                    formData.append('posters', values.banner);
                }

                if (values.galleryImages && values.galleryImages.length > 0) {
                    values.galleryImages.forEach((file) => {
                        if (file) formData.append('images[]', file);
                    });
                }

                // console.log("FormData payload:", formData);

                await dispatch(
                    updateEventById({
                        eventId: eventData?.eventId || 70,
                        eventData: formData
                    })
                ).unwrap();

                dispatch(nextTab());
            } catch (error) {
                errorToast(error?.data?.message || 'Failed to update media. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    });

    // console.log("Errors : ", formik.values);

    function handleEventThumbnailChange(file) {
        if (!file) return false;

        const reader = new FileReader();
        reader.onload = (e) => {
            setCropImage(e.target.result);
            setCropType('thumbnail');
        };

        reader.readAsDataURL(file);
        formik.setFieldValue('thumbnail', file);
    }

    function handleEventBannerChange(file) {
        if (!file) return false;

        const reader = new FileReader();
        reader.onload = (e) => {
            setCropImage(e.target.result);
            setCropType('banner');
        };

        reader.readAsDataURL(file);
        formik.setFieldValue('banner', file);
    }

    // function to handle event layout change
    function handleEventLayoutChange(file) {
        if (!file) return false;

        const reader = new FileReader();
        reader.onload = (e) => {
            setCropImage(e.target.result);
            setCropType('layout');
        };

        reader.readAsDataURL(file);
        formik.setFieldValue('layoutThumbnail', file);
    }

    // function to handle gallery image change and drop all the selected files object into the formik field
    function handleGalleryImageChange(file, index) {
        if (!file) return false;

        const reader = new FileReader();
        reader.onload = (e) => {
            setCropImage(e.target.result);
            setCropType('gallery');
            setCropIndex(index);
        };
        reader.readAsDataURL(file);
        formik.setFieldValue(`galleryImages[${index}]`, file);
    }

    // function to handle the crop complete event and set the cropped image to the respective state
    function handleCropComplete(cropResult) {
        const { file, previewUrl } = cropResult;

        if (!file || !previewUrl) {
            errorToast('Failed to process cropped image.');
            return;
        }

        if (cropType === 'thumbnail') {
            setEventThumbnail({ previewUrl });
            formik.setFieldValue('thumbnail', file); // Set the FILE for upload
            successToastTopRight('Image uploaded successfully');
        } else if (cropType === 'layout') {
            setEventLayout({ previewUrl });
            formik.setFieldValue('layoutThumbnail', file);
            successToastTopRight('Image uploaded successfully');
        } else if (cropType === 'banner') {
            setBanner({ previewUrl });
            formik.setFieldValue('banner', file);
            successToastTopRight('Image uploaded successfully');
        } else if (cropType === 'gallery') {
            setEventGalleryImages((prevImages) => {
                const newImages = [...prevImages];
                newImages[cropIndex] = { previewUrl };
                return newImages;
            });
            formik.setFieldValue(`galleryImages[${cropIndex}]`, file);
            successToastTopRight('Image uploaded successfully');
        }

        setCropImage(null);
        setCropType(null);
        setCropIndex(null);
    }

    // function to add event gallery image and set the state. maximum 3 image is the limit
    function addGalleryImage() {
        // if (!isEditModeOn) return false;

        if (eventGalleryImages.length < 3) {
            setEventGalleryImages([...eventGalleryImages, null]);
            formik.setFieldValue('galleryImages', [...formik.values.galleryImages, null]);
        }
        return null;
    }

    // remove the event gallery image based on index
    function removeEventGalleryImage(index) {
        // if (!isEditModeOn) return false;

        if (index === 0 && eventGalleryImages.length === 1) {
            if (eventGalleryImages[0]) {
                setEventGalleryImages([null]);
                successToastTopRight('Image removed successfully');
                formik.setFieldValue('galleryImages', [null]);
            } else {
                setEventGalleryImages([null]);
                formik.setFieldValue('galleryImages', [null]);
            }
        } else {
            if (index > 0) {
                const newGalleryImages = [...eventGalleryImages];
                if (newGalleryImages[index]) {
                    newGalleryImages.splice(index, 1);
                    setEventGalleryImages(newGalleryImages);
                    formik.setFieldValue('galleryImages', newGalleryImages);
                    successToastTopRight('Image removed successfully');
                } else {
                    newGalleryImages.splice(index, 1);
                    formik.setFieldValue('galleryImages', newGalleryImages);
                    setEventGalleryImages(newGalleryImages);
                }
            } else {
                const newGalleryImages = [...eventGalleryImages];
                if (newGalleryImages[index]) {
                    newGalleryImages[index] = null;
                    setEventGalleryImages(newGalleryImages);
                    formik.setFieldValue('galleryImages', newGalleryImages);
                    successToastTopRight('Image removed successfully');
                } else {
                    newGalleryImages[index] = null;
                    formik.setFieldValue('galleryImages', newGalleryImages);
                    setEventGalleryImages(newGalleryImages);
                }
            }
        }
    }

    // remove the banner
    function removeBanner() {
        // if (!isEditModeOn) return false;
        setBanner(null);
        formik.setFieldValue('banner', null);
        successToastTopRight('Image removed successfully');
    }

    // remove the event thumbnail
    function removeEventThumbnail() {
        // if (!isEditModeOn) return false;
        setEventThumbnail(null);
        formik.setFieldValue('thumbnail', null);
        successToastTopRight('Image removed successfully');
    }

    // remove the event layout
    function removeEventLayout() {
        // if (!isEditModeOn) return false;
        setEventLayout(null);
        formik.setFieldValue('layoutThumbnail', null);
        successToastTopRight('Image removed successfully');
    }

    // reset the crop state if the crop modal is closed
    function onCropClose() {
        setCropImage(null);
        setCropType(null);
        setCropIndex(null);
    }

    // useEffect to handle the event gallery horizontal scroll and prevent vertical scroll if there's more than one element
    useEffect(() => {
        function handleWheel(event) {
            // Only disable vertical scroll and enable horizontal scroll if there's more than one element
            if (eventGalleryImages.length > 1) {
                if (event.deltaY !== 0) {
                    // scroll the event gallery scroll container by the deltaY value
                    eventGalleryScrollRef.current.scrollBy({
                        left: event.deltaY,
                        behavior: 'smooth'
                    });
                    // prevent the default behavior of the wheel event
                    event.preventDefault();
                }
            }
        }

        // add the wheel event listener to the event gallery scroll container if the container is available using wheel event
        const eventGalleryScroll = eventGalleryScrollRef.current;

        // add the wheel event listener to the event gallery scroll container
        eventGalleryScroll.addEventListener('wheel', handleWheel);

        // remove the wheel event listener from the event gallery scroll container on unmount
        return () => {
            eventGalleryScroll.removeEventListener('wheel', handleWheel);
        };
    }, [eventGalleryImages.length]);

    // return the required values and functions
    return {
        loading,
        formik,
        isEditModeOn,
        setIsEditModeOn,
        cropType,
        cropImage,
        eventLayout,
        banner,
        eventThumbnail,
        eventGalleryImages,
        eventGalleryScrollRef,
        removeBanner,
        onCropClose,
        addGalleryImage,
        removeEventLayout,
        handleCropComplete,
        removeEventThumbnail,
        removeEventGalleryImage,
        handleEventLayoutChange,
        handleGalleryImageChange,
        handleEventThumbnailChange,
        handleEventBannerChange,
        shouldShowEditButton
    };
}
